<!-- Copyright: Seán I. O'Donoghue -->
<!-- A poster's zoomable image (spec 2026-10-26; posterViewer.js): its box the poster's own -->
<!-- fitted size (the page gives both), so its controls sit on the image's corners and a click -->
<!-- on it is a click on the image; sharpening as its tiles arrive; shown only on the page (it -->
<!-- scrolls past), explored in OpenSeadragon's own full screen (a click on the image, or the -->
<!-- button top right), with our controls over it: zoom, Fit and Exit. -->
<template>
  <section class="stage" :aria-label="label">
    <div
      class="viewerBox"
      :class="{isShort: height < SHORT}"
      :style="{width: `${width}px`, height: `${height}px`}">
      <!-- the controls inside the viewer's element: OpenSeadragon's full screen moves it to the
           body, and they go with it. A click on the page's image opens full screen -->
      <div
        ref="container"
        class="viewer"
        :class="{isFullscreen}"
        :title="isFullscreen ? '' : 'View fullscreen'"
        @click="onViewerClick">
        <div class="controls" @click.stop>
          <template v-if="isFullscreen">
            <button
              v-for="control in CONTROLS"
              :key="control.label"
              type="button"
              :aria-label="control.label"
              :title="control.label"
              @click="control.act()">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="control.icon" /></svg>
            </button>
          </template>
          <button
            v-if="isReady"
            type="button"
            :aria-label="fullscreenLabel"
            :title="fullscreenLabel"
            @click="toggleFullscreen">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path :d="isFullscreen ? ICONS.exit : ICONS.enter" />
            </svg>
          </button>
        </div>
      </div>
      <!-- the download, bottom right over the image: beside the viewer, not in it (a click on
           the viewer opens full screen), so on the page only -->
      <a
        :href="downloadUrl"
        class="download"
        aria-label="Download image"
        title="Download image"
        data-same-tab
        @click.prevent="openDownload">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="ICONS.download" /></svg>
      </a>
      <p v-if="isLoading && !hasFailed" class="loading" role="status">Loading the poster…</p>
      <div v-if="hasFailed" class="failed" role="alert">
        <img :src="thumbnailUrl" alt="" />
        <p>
          The zoomable image couldn't be loaded.
          <button type="button" @click="retry">Retry</button>
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import {
  computed, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, watch,
} from "vue";
import {
  fetchOverview, fitImage, getViewer, refit, setInteractive, updateNavigator, zoomIn, zoomOut,
} from "@/composables/posterViewer.js";
import {getPosterFileUrl, getThumbnailUrl} from "@/composables/usePosters.js";

const props = defineProps({
  poster: {type: Object, required: true}, // a posters.json entry
  width: {type: Number, required: true}, // px: the image's box on the page, the poster's
  height: {type: Number, required: true}, // aspect ratio (PosterPage.vue)
  downloadUrl: {type: String, required: true} // the full-size image (or GIF), old server
});

const ICONS = {
  out: "M5 11h14v2H5z",
  in: "M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z",
  fit: "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z", // home: the whole poster (not like full screen)
  enter: "M3 3h7v2H5v5H3zm11 0h7v7h-2V5h-5zM3 14h2v5h5v2H3zm16 0h2v7h-7v-2h5z",
  exit: "M8 3h2v7H3V8h5zm6 0h2v5h5v2h-7zM3 14h7v7H8v-5H3zm11 0h7v2h-5v5h-2z",
  download: "M11 4h2v9l3.5-3.5 1.4 1.4L12 16.8l-5.9-5.9 1.4-1.4L11 13zM5 18h14v2H5z",
};
const SHORT = 112; // px: a poster too short for its controls at two corners (e.g. 2010 T06
// on a phone): the download beside full screen, top right
// our own old server, in the same tab (CLAUDE.md § Navigation conventions)
const openDownload = () => (window.location.href = props.downloadUrl);
const [container, isLoading, hasFailed, isReady, isFullscreen] = [
  ref(null), ref(true), ref(false), ref(false), ref(false)];
const fullscreenLabel = computed(() => (isFullscreen.value ? "Exit fullscreen" : "View fullscreen"));
const label = computed(() => `Poster ${props.poster.posterNum}: ${props.poster.title}`);
const thumbnailUrl = computed(() => getThumbnailUrl(props.poster, 600));
let viewer = null;
const CONTROLS = [
  {label: "Zoom out", icon: ICONS.out, act: () => zoomOut(viewer)},
  {label: "Zoom in", icon: ICONS.in, act: () => zoomIn(viewer)},
  {label: "Fit image", icon: ICONS.fit, act: () => fitImage(viewer)},
];
const toggleFullscreen = () => viewer?.setFullScreen(!viewer.isFullPage());
// the page's image: a click (or a tap) opens full screen; there, clicks are OpenSeadragon's
const onViewerClick = () => isReady.value && !isFullscreen.value && viewer.setFullScreen(true);

function retry() {
  [hasFailed.value, isLoading.value] = [false, true];
  viewer.open(getPosterFileUrl(props.poster, "xml"));
}

// once the page's layout is back (two frames after leaving full screen): the whole poster
const refitWhenSettled = () => requestAnimationFrame(() => requestAnimationFrame(() => {
  if (viewer) refit(viewer);
}));

// entering or leaving full screen, the window keeps resizing for a moment (the browser's own
// animation): the whole poster after each step, until it's done
const SETTLE_MS = 1000;
let settlingUntil = 0;

// full screen (OpenSeadragon's: Esc or its button leaves it): the image explored, starting
// whole; on leaving it, shown whole again, not interactive. The window's width set free while the viewer fills
// it (App.vue caps the body's; global.css)
function onFullPage({fullPage}) {
  isFullscreen.value = fullPage;
  settlingUntil = Date.now() + SETTLE_MS;
  setInteractive(viewer, fullPage);
  document.body.classList.toggle("isPosterFullPage", fullPage);
  refitWhenSettled();
}

// on the page, or while full screen settles (later, a resize leaves the image as explored)
function goHomeOnResize() {
  if (!viewer || (viewer.isFullPage() && Date.now() > settlingUntil)) return;
  viewer.viewport.goHome(true);
}

function addHandlers() {
  viewer.addHandler("open", () => [isReady.value, hasFailed.value] = [true, false]);
  viewer.addHandler("open-failed", () => [hasFailed.value, isLoading.value] = [true, false]);
  viewer.addHandler("tile-loaded", () => (isLoading.value = false));
  viewer.addHandler("zoom", () => updateNavigator(viewer));
  viewer.addHandler("open", () => updateNavigator(viewer));
  viewer.addHandler("full-page", onFullPage);
  // the window resized: the whole poster again; a frame later, as OpenSeadragon then keeps the
  // old zoom (doViewerResize)
  viewer.addHandler("after-resize", () => requestAnimationFrame(goHomeOnResize));
}

// a new size for the box (the page measured it, or the window changed): the whole poster again
watch(() => [props.width, props.height], () => nextTick(() => viewer?.viewport && refit(viewer)));

// the poster opened once its overview is in (unless the viewer was closed meanwhile)
async function openPoster(openedViewer) {
  await fetchOverview(props.poster);
  if (viewer === openedViewer) viewer.open(getPosterFileUrl(props.poster, "xml"));
}

// kept alive by Main.vue: the viewer (and its tiles) only while the page is shown, the poster
// fitted again each time
function openViewer() {
  if (viewer) return;
  [isLoading.value, hasFailed.value, isReady.value] = [true, false, false];
  viewer = getViewer(container.value);
  setInteractive(viewer, false);
  addHandlers();
  openPoster(viewer);
}
function closeViewer() {
  if (viewer?.isFullPage()) viewer.setFullScreen(false);
  document.body.classList.remove("isPosterFullPage");
  viewer?.destroy();
  viewer = null;
}

onMounted(openViewer); // shown inside a page already active: onActivated isn't called
onActivated(openViewer);
onDeactivated(closeViewer);
onBeforeUnmount(closeViewer);
</script>

<style scoped>
.viewerBox {
  position: relative;
  max-width: 100%;
  margin-inline: auto; /* centred in its column: the page's surface around it */
}
/* while its box resizes (full screen in or out), the browser shows the last frame until
   OpenSeadragon redraws: scaled whole, never stretched */
.viewer :deep(canvas) {
  object-fit: contain;
}
/* the page's surface while it loads (PosterPage.vue's --poster-surface); in full screen, where
   the viewer is moved to the body, out of the page, dark around the poster */
.viewerBox,
.viewer {
  background: var(--poster-surface, #111);
}
.viewer {
  position: absolute;
  inset: 0;
}
/* its own "Unable to open…" message: ours instead, with Retry */
.viewer :deep(.openseadragon-message) {
  display: none;
}
.loading,
.failed p {
  position: absolute;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%);
  margin: 0;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #ddd;
  background: rgb(0 0 0 / 0.6);
  white-space: nowrap;
}
.loading {
  pointer-events: none; /* a click on it is the image's: full screen */
  top: 50%;
  bottom: auto;
  transform: translate(-50%, -50%);
}
.failed img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
/* the page's image is a way into full screen */
.viewer:not(.isFullscreen),
.viewer:not(.isFullscreen) :deep(.openseadragon-canvas) {
  cursor: pointer;
}
/* top right on the image: understated, but always in sight; the same place and size in both
   states (global.css styles every button: reset here) */
.controls {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 5; /* over OpenSeadragon's canvas, which it adds after them */
  display: flex;
  gap: 0.3rem;
}
.download {
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid rgb(255 255 255 / 0.25);
  border-radius: 4px;
  background: rgb(0 0 0 / 0.55);
  color: #eee;
}
.isShort .download {
  top: 0.5rem;
  bottom: auto;
  right: 3.3rem; /* the full screen button's width and the controls' gap */
}
.download:hover {
  background: rgb(0 0 0 / 0.8);
  color: white;
}
.download:focus-visible {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}
.download svg {
  width: 1.3rem;
  height: 1.3rem;
  fill: currentColor;
}
.controls button,
.failed button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgb(255 255 255 / 0.25);
  border-radius: 4px;
  box-shadow: none;
  background: rgb(0 0 0 / 0.55);
  color: #eee;
  cursor: pointer;
}
.controls button {
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
}
.controls button:hover {
  background: rgb(0 0 0 / 0.8);
  color: white; /* hover: brighter, never green */
}
.failed button {
  height: 1.8rem;
  margin-left: 0.5rem;
  padding: 0 0.5rem;
  color: #fff;
}
.controls button:focus-visible,
.failed button:focus-visible {
  outline: 2px solid var(--brand-color);
  outline-offset: 2px;
}
.controls svg {
  width: 1.3rem;
  height: 1.3rem;
  fill: currentColor;
}
</style>
